import { useState } from 'react';
import type { JSX } from 'react';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { FeedbackPalette } from '../../types.js';

const RATING_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

interface NumericRatingProps {
  palette: FeedbackPalette;
  value: number | null;
  onChange: (rating: number) => void;
  min: number;
  max: number;
  leftLabel?: string;
  rightLabel?: string;
  buttonSize?: 'default' | 'compact';
}

export function NumericRating({
  palette,
  value,
  onChange,
  min,
  max,
  leftLabel,
  rightLabel,
  buttonSize = 'default',
}: NumericRatingProps): JSX.Element {
  const items = Array.from({ length: max - min + 1 }, (_, index) => min + index);
  const height = buttonSize === 'compact' ? 38 : 48;
  const fontSize = buttonSize === 'compact' ? 13 : 16;
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/*
        flex-wrap: nowrap + flex: 1 1 0 + minWidth: 0 keeps NPS 0–10 on a
        single line down to ~320 px iframes; each cell shrinks evenly so
        no value ever drops onto a second row.
      */}
      <div
        className="chirp-fb-numeric-row"
        style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', alignItems: 'stretch' }}
        onMouseLeave={() => setHovered(null)}
      >
        {items.map((number) => {
          const selected = value === number;
          const isHovered = hovered === number && !selected;
          // Three tiers: idle (muted border + text), hover preview (60%
          // accent border + faint accent tint), selected (filled accent).
          const borderColor = selected
            ? palette.primary
            : isHovered
              ? hexWithAlpha(palette.primary, 0.6)
              : palette.border;
          const background = selected
            ? palette.primary
            : isHovered
              ? hexWithAlpha(palette.primary, 0.08)
              : 'transparent';
          const color = selected
            ? palette.onPrimary
            : isHovered
              ? palette.primary
              : palette.text;
          return (
            <button
              key={number}
              type="button"
              onClick={() => onChange(number)}
              onMouseEnter={() => setHovered(number)}
              className="chirp-fb-numeric-button"
              style={{
                flex: '1 1 0',
                minWidth: 0,
                height,
                padding: 0,
                borderRadius: palette.radius,
                border: `1.5px solid ${borderColor}`,
                background,
                color,
                fontSize,
                fontWeight: 600,
                cursor: 'pointer',
                transition: `border-color 220ms ${RATING_EASE}, background-color 220ms ${RATING_EASE}, color 220ms ${RATING_EASE}`,
              }}
            >
              {number}
            </button>
          );
        })}
      </div>
      {(leftLabel || rightLabel) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: palette.textMuted }}>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
    </div>
  );
}
